import React, { useState, useEffect, useMemo } from "react";
import style from "css/admin/component.module.css";
import {
  Modal,
  Form,
  Button,
  ConfigProvider,
  Cascader,
  Select,
  message,
} from "antd";
import SearchInput from "component/admin/search/searchInput";
import SearchDatePicker from "component/admin/search/searchDatePicker";
import dayjs from "dayjs";
import axios from "axios";

function PeriodModal({
  isOpen,
  close,
  mode = "create",
  initialData = null,
  onSuccess,
}) {
  const [form] = Form.useForm();
  const [licenseOptions, setLicenseOptions] = useState([]);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [campusOptions, setCampusOptions] = useState([]);
  const [campusLoading, setCampusLoading] = useState(false);
  const [licenseCounts, setLicenseCounts] = useState({});

  const closeHandler = () => {
    form.resetFields();
    setLicenseCounts({});
    close();
  };

  // 선택된 라이선스 목록 (Cascader value 감시)
  const selectedProduct = Form.useWatch("product", form);
  const selectedLicenses = useMemo(() => {
    if (!Array.isArray(selectedProduct)) return [];
    return selectedProduct
      .filter((pair) => pair?.[0] && pair?.[1])
      .map((pair) => {
        const manuCd = pair[0];
        const prodCd = pair[1];
        // licenseOptions에서 라벨 찾기
        const manuGroup = licenseOptions.find((g) => g.value === manuCd);
        const manuLabel = manuGroup?.label ?? manuCd;
        const prodItem = manuGroup?.children?.find((c) => c.value === prodCd);
        const prodLabel = prodItem?.label ?? prodCd;
        return {
          manuCd,
          prodCd,
          manuLabel,
          prodLabel,
          key: `${manuCd}|${prodCd}`,
        };
      });
  }, [selectedProduct, licenseOptions]);

  // 수정 모드일 때 기존 데이터로 form 초기화
  useEffect(() => {
    if (!isOpen) return;
    if (mode !== "edit" || !initialData) return;

    const licList = Array.isArray(initialData?.LICENSE_LIST)
      ? initialData.LICENSE_LIST
      : [];
    const productPairs = licList
      .map((v) => [v?.MANUFACTURER_CD, v?.PRODUCT_CD])
      .filter((pair) => pair?.[0] && pair?.[1]);

    // LICENSE_CNT 복원
    const counts = {};
    for (const v of licList) {
      if (v?.MANUFACTURER_CD && v?.PRODUCT_CD) {
        counts[`${v.MANUFACTURER_CD}|${v.PRODUCT_CD}`] = v.LICENSE_CNT ?? 0;
      }
    }
    setLicenseCounts(counts);

    form.setFieldsValue({
      periodName: initialData?.PERIOD_NM ?? "",
      campusCd: initialData?.CAMPUS_CD || "",
      startDate: initialData?.START_DT ? dayjs(initialData.START_DT) : null,
      endDate: initialData?.END_DT ? dayjs(initialData.END_DT) : null,
      activeDate: initialData?.ACTIVE_DT ? dayjs(initialData.ACTIVE_DT) : null,
      expireDate: initialData?.EXPIRE_DT ? dayjs(initialData.EXPIRE_DT) : null,
      product: productPairs,
    });
  }, [isOpen, mode, initialData, form]);

  // 모달이 열릴 때 캠퍼스 목록 조회
  useEffect(() => {
    if (!isOpen) return;
    if (campusOptions.length > 0) return;

    setCampusLoading(true);
    const config = { params: { GROUP_CD: "CD06" } };
    axios
      .get("/code", config)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          const items = [
            { value: "", label: "전체" },
            ...res.data.map((row) => ({
              value: row?.CODE_CD,
              label: row?.VIEW_NM,
            })),
          ];
          setCampusOptions(items);
        } else {
          setCampusOptions([{ value: "", label: "전체" }]);
        }
      })
      .catch((err) => {
        console.log(err);
        setCampusOptions([{ value: "", label: "전체" }]);
      })
      .finally(() => setCampusLoading(false));
  }, [isOpen, campusOptions.length]);

  // 모달이 열릴 때 라이선스 목록 조회 후 제조사(상위) -> 제품(하위) 2 depth options 생성
  useEffect(() => {
    if (!isOpen) return;
    // 이미 로드된 상태면 재호출하지 않음
    if (licenseOptions.length > 0) return;

    setLicenseLoading(true);
    axios
      .get("/product/detail")
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          const manufacturerMap = new Map();
          for (const row of res.data) {
            const manuCd = row?.MANUFACTURER_CD;
            if (!manuCd) continue;
            let group = manufacturerMap.get(manuCd);
            if (!group) {
              group = {
                label: row?.MANUFACTURER_NM ?? manuCd,
                value: manuCd,
                children: [],
              };
              manufacturerMap.set(manuCd, group);
            }
            const prodNm = row?.PRODUCT_NM ?? row?.PRODUCT_CD;
            group.children.push({
              // 드롭다운에서는 제품명만 보이게
              label: prodNm,
              value: row?.PRODUCT_CD,
            });
          }
          setLicenseOptions(Array.from(manufacturerMap.values()));
        } else {
          setLicenseOptions([]);
        }
      })
      .catch((err) => {
        console.log(err);
        setLicenseOptions([]);
      })
      .finally(() => setLicenseLoading(false));
  }, [isOpen, licenseOptions.length]);

  const submitPeriod = async () => {
    try {
      const values = await form.validateFields();
      const toDateTime = (d) =>
        d ? dayjs(d).format("YYYY-MM-DD HH:mm:ss") : "";

      const licenseValue = values?.product;
      // Cascader multiple value: [[MANUFACTURER_CD, PRODUCT_CD], ...]
      const LICENSE_LIST = (Array.isArray(licenseValue) ? licenseValue : [])
        .map((pair) => ({
          MANUFACTURER_CD: pair?.[0],
          PRODUCT_CD: pair?.[1],
          LICENSE_CNT: Number(licenseCounts[`${pair?.[0]}|${pair?.[1]}`] ?? 0),
        }))
        .filter((v) => v.MANUFACTURER_CD && v.PRODUCT_CD);

      // 수량 0인 항목 체크
      const zeroCnt = LICENSE_LIST.find(
        (v) => !v.LICENSE_CNT || v.LICENSE_CNT <= 0,
      );
      if (zeroCnt) {
        message.error("라이선스 수량을 입력하세요.");
        return;
      }

      const data = {
        PERIOD_NM: values?.periodName,
        CAMPUS_CD: values?.campusCd || null,
        START_DT: toDateTime(values?.startDate),
        END_DT: toDateTime(values?.endDate),
        ACTIVE_DT: toDateTime(values?.activeDate),
        EXPIRE_DT: toDateTime(values?.expireDate),
        LICENSE_LIST,
      };

      const res =
        mode === "edit" && initialData?.PERIOD_ID
          ? await axios.put(`/period/${initialData.PERIOD_ID}`, data)
          : await axios.post("/period", data);
      if (res.status === 200) {
        message.success(
          mode === "edit"
            ? "신청 기간이 성공적으로 수정되었습니다."
            : "신청 기간이 성공적으로 등록되었습니다.",
        );
        if (typeof onSuccess === "function") onSuccess();
        closeHandler();
      } else {
        message.error(
          mode === "edit"
            ? "신청 기간 수정에 실패했습니다."
            : "신청 기간 등록에 실패했습니다.",
        );
      }
    } catch (err) {
      if (err?.errorFields) {
        message.error("필수 항목을 확인해주세요.");
        return;
      }
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.response?.data ||
        err?.message;
      message.error(
        serverMsg
          ? `신청 기간 등록 오류: ${serverMsg}`
          : "신청 기간 등록 중 오류가 발생했습니다.",
      );
      console.log(err);
    }
  };

  return (
    <Modal
      title={
        <span className={style.searchItem_label} style={{ fontSize: 18 }}>
          {mode === "edit" ? "신청 기간 수정" : "신청 기간 등록"}
        </span>
      }
      open={isOpen}
      onCancel={closeHandler}
      closable={false}
      styles={{
        content: { borderRadius: 12 },
        mask: { backdropFilter: "none" },
      }}
      footer={
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: "#527ff2",
            },
            components: {
              Button: {
                colorPrimaryHover: "#4a72de",
                colorPrimaryActive: "#3f66c8",
              },
            },
          }}
        >
          <div style={{ display: "flex", gap: 8, width: "100%" }}>
            <Button
              key="cancel"
              onClick={closeHandler}
              style={{
                flex: 1,
                height: 40,
                borderRadius: 5,
                fontSize: 18,
              }}
            >
              취소
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={submitPeriod}
              style={{
                flex: 1,
                height: 40,
                borderRadius: 5,
                fontSize: 18,
              }}
            >
              {mode === "edit" ? "수정" : "등록"}
            </Button>
          </div>
        </ConfigProvider>
      }
      centered
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        style={{ marginTop: 10 }}
      >
        <Form.Item
          name="campusCd"
          label={<span className={style.searchItem_label}>캠퍼스</span>}
          initialValue=""
        >
          <Select
            className={style.search_cascader}
            options={campusOptions}
            loading={campusLoading}
            disabled={campusLoading}
          />
        </Form.Item>
        <Form.Item
          name="periodName"
          label={<span className={style.searchItem_label}>제목</span>}
          rules={[{ required: true, message: "제목을 입력하세요" }]}
        >
          <SearchInput placeholder="예) 2025년 1학기" fullWidth />
        </Form.Item>
        <Form.Item
          name="startDate"
          label={<span className={style.searchItem_label}>신청 시작일</span>}
          rules={[{ required: true, message: "신청 시작일을 선택하세요" }]}
        >
          <SearchDatePicker width="100%" showTime={{ format: "HH:mm" }} format="YYYY-MM-DD HH:mm" placeholder="YYYY-MM-DD HH:mm" />
        </Form.Item>
        <Form.Item
          name="endDate"
          label={<span className={style.searchItem_label}>신청 종료일</span>}
          rules={[{ required: true, message: "신청 종료일을 선택하세요" }]}
        >
          <SearchDatePicker width="100%" showTime={{ format: "HH:mm" }} format="YYYY-MM-DD HH:mm" placeholder="YYYY-MM-DD HH:mm" />
        </Form.Item>
        <Form.Item
          name="activeDate"
          label={
            <span className={style.searchItem_label}>라이선스 시작일</span>
          }
          rules={[{ required: true, message: "라이선스 시작일을 선택하세요" }]}
        >
          <SearchDatePicker width="100%" />
        </Form.Item>
        <Form.Item
          name="expireDate"
          label={
            <span className={style.searchItem_label}>라이선스 종료일</span>
          }
          rules={[{ required: true, message: "라이센스 종료일을 선택하세요" }]}
        >
          <SearchDatePicker width="100%" />
        </Form.Item>
        <Form.Item
          name="product"
          label={<span className={style.searchItem_label}>라이선스</span>}
          rules={[
            {
              validator: (_, v) =>
                Array.isArray(v) && v.length > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error("라이센스를 1개 이상 선택하세요")),
            },
          ]}
        >
          <Cascader
            className={style.search_cascader}
            options={licenseOptions}
            displayRender={(labels) => labels.join(" ")}
            multiple
            showCheckedStrategy={Cascader.SHOW_CHILD}
            maxTagCount="responsive"
            placeholder={
              licenseLoading
                ? "라이선스 목록 불러오는 중..."
                : "제조사/제품을 선택하세요"
            }
            disabled={licenseLoading}
          />
        </Form.Item>
        {selectedLicenses.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <span
              className={style.searchItem_label}
              style={{ display: "block", marginBottom: 8 }}
            >
              라이선스 수량
            </span>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {selectedLicenses.map((lic) => (
                <div
                  key={lic.key}
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <span
                    className={style.searchItem_label}
                    style={{ minWidth: 120, fontSize: 13 }}
                  >
                    {lic.manuLabel} {lic.prodLabel}
                  </span>
                  <SearchInput
                    type="number"
                    min="0"
                    placeholder="수량"
                    value={licenseCounts[lic.key] ?? ""}
                    onChange={(e) => {
                      setLicenseCounts((prev) => ({
                        ...prev,
                        [lic.key]: e.target.value,
                      }));
                    }}
                    style={{ flex: 1 }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </Form>
    </Modal>
  );
}

export default PeriodModal;
